import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import es from "react-phone-input-2/lang/es.json";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TrashIcon from "../../assets/icon/TrashIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    borderRadius: 16,
    width: "100%",
    maxWidth: 720,
    overflow: "hidden",
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  dialogTitle: {
    backgroundColor: theme.palette.tabHeaderBackground,
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& h2": {
      fontSize: 20,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  dialogContent: {
    backgroundColor: theme.palette.tabHeaderBackground,
    padding: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  fieldColumn: {
    minWidth: 0,
  },
  fullWidthRow: {
    marginBottom: theme.spacing(2),
  },
  extraInfoSection: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  extraAttr: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: theme.spacing(1.5),
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
    },
  },

  btnWrapper: {
    position: "relative",
    borderRadius: 10,
    padding: 10,
    width: 100,
  },
  noHover: {
    minWidth: 0,
    paddingRight: 5,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  textField: {
    height: "56px",
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      "& fieldset": {
        borderColor: "#00000014",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  addExtraButton: {
    width: "100%",
    height: 44,
    borderRadius: 10,
    textTransform: "none",
  },
  extraAction: {
    marginTop: theme.spacing(0.5),
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  phoneInputContainer: {
    width: "100%",
    marginTop: 4,
    color: theme.palette.text.main,
    border: theme.palette.text.main,
    background: "transparent",
    "& .form-control": {
      width: "100%",
      height: '42px !important',
      borderRadius: 10,
      borderColor: theme.palette.borderPhoneInput,
      transition: "border-color 0.3s ease",
      "&:focus": {
        borderColor: theme.palette.primary.main,
        outline: "none",
        boxShadow: "none",
        borderWidth: "2px",
      },
      "&:hover": {
        borderColor: theme.palette.borderPrimary,
      },
      "&:focus:hover": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .special-label": {
      backgroundColor: theme.palette.inputBackground,
      color: theme.palette.text.secondary,
      marginLeft: "-11px",
    },
    "& .flag-dropdown": {
      borderRadius: "10px 0 0 10px",
			height: '42px !important',
    },
    [theme.breakpoints.down("sm")]: {
      "& .form-control": {
        fontSize: "14px",
        height: "48px",
      },
    },
  },
  phoneInputError: {
    "& .form-control": {
      borderColor: theme.palette.error.main,
    },
  },
  dialogActions: {
    backgroundColor: theme.palette.tabHeaderBackground,
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
      flexDirection: "column-reverse",
      alignItems: "stretch",
      "& > *": {
        width: "100%",
        marginLeft: "0 !important",
        marginBottom: theme.spacing(1),
      },
    },
  },
}));

// Función para detectar si un contacto es de webchat
const isWebChatContact = (number) => {
  return number && number.startsWith("webchat:");
};

const getInitialContact = (values) => ({
  name: values?.name || "",
  number: values?.number || "+",
  email: values?.email || "",
  extraInfo: values?.extraInfo || [],
});

// Esquema de validación dinámico
const getContactSchema = (isWebChat) => {
  const baseSchema = {
    name: Yup.string()
      .min(2, "¡Muy corto!")
      .max(50, "¡Demasiado largo!")
      .required("Requerido"),
    email: Yup.string().email("Correo electrónico inválido"),
  };

  if (!isWebChat) {
    baseSchema.number = Yup.string()
      .min(9, "¡Muy corto!")
      .max(50, "¡Demasiado largo!")
      .required("El número es requerido");
  }

  return Yup.object().shape(baseSchema);
};

const ContactModal = ({ open, onClose, contactId, initialValues }) => {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const [contact, setContact] = useState(() => getInitialContact(initialValues));
  const [isLoading, setIsLoading] = useState(false);

  // Detectar si es contacto de webchat
  const isWebChat = isWebChatContact(contact?.number);

  useEffect(() => {
    let isMounted = true;

    const fetchContact = async () => {
      if (!contactId) {
        if (isMounted) {
          setContact(getInitialContact(initialValues));
        }
        return;
      }
      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (!isMounted) return;

        setContact({
          name: data.name || "",
          number: formatPhoneNumber(data.number) || "",
          email: data.email || "",
          extraInfo: data.extraInfo || [],
        });
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      }
    };

    fetchContact();

    return () => {
      isMounted = false;
    };
  }, [contactId, initialValues]);
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    return phoneNumber.replace(/[^0-9+]/g, '').replace(/^(\+)/, '');
  };

  const handleSaveContact = async (values) => {
    // Si es webchat, mantener el número original
    setIsLoading(true);
    const dataToSend = isWebChat
      ? {
          ...values,
          number: contact.number, // Mantener el número original de webchat
        }
      : values;

    const contactData = { ...dataToSend };

    try {
      if (contactId) {
        const { data } = await api.put(`/contacts/${contactId}`, contactData);
        toast.success("El contacto se ha editado correctamente");
        onClose(true, data); // Pasamos el contacto actualizado con ID
      } else {
        const { data } = await api.post("/contacts", contactData);
        toast.success("El contacto se ha creado correctamente");
        onClose(true, data); // Pasamos el nuevo contacto creado con ID
      }
    } catch (err) {
      toastError(err);
      // El modal permanece abierto para permitir al usuario corregir los datos
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {contactId
            ? `${i18n.t("contactModal.title.edit")}`
            : `${i18n.t("contactModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={getContactSchema(isWebChat)}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveContact(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
          }) => (
						<Form>
							<DialogContent dividers className={classes.dialogContent}>
								<Typography
									variant="subtitle1"
									className={classes.sectionTitle}
								>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<div className={classes.formRow}>
									<div className={classes.fieldColumn}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.name")}
											name="name"
											autoFocus
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
											className={classes.textField}
										/>
									</div>

									<div className={classes.fieldColumn}>
										{/* Solo mostrar el campo de número si NO es contacto de webchat */}
										{!isWebChat && (
											<>
												<PhoneInput
													country={"mx"}
													value={values.number || ""}
													inputProps={{
														name: "number",
														required: true,
														autoFocus: false,
														color: theme.palette.text.secondary,
													}}
													specialLabel={i18n.t("contactModal.form.number")}
													localization={es}
													containerClass={`${classes.phoneInputContainer} ${errors.number ? classes.phoneInputError : ""}`}
													inputStyle={{
														width: "100%",
														height: "56px",
														fontSize: "16px",
														paddingLeft: "48px",
														background: theme.palette.inputBackground,
														color: theme.palette.text.secondary,
													}}
													buttonStyle={{
														color: theme.palette.text.main,
														backgroundColor: "transparent",
														border: "none",
														position: "absolute",
														left: "0",
														top: "0",
														height: "56px",
													}}
													onChange={(phone) => setFieldValue("number", phone)}
													isValid={() => !errors.number}
												/>
												{errors.number && (
													<FormHelperText error={Boolean(errors.number)}>
														{errors.number}
													</FormHelperText>
												)}
											</>
										)}

										{/* Mostrar información del canal si es webchat */}
										{isWebChat && (
											<TextField
												label="Canal"
												value="WebChat"
												variant="outlined"
												margin="dense"
												fullWidth
												className={classes.textField}
												disabled
											/>
										)}
									</div>
								</div>

								<div className={classes.fullWidthRow}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
										className={classes.textField}
									/>
								</div>

								<div className={classes.extraInfoSection}>
									<Typography
										variant="subtitle1"
										className={classes.sectionTitle}
									>
										{i18n.t("contactModal.form.extraInfo")}
									</Typography>

									<FieldArray name="extraInfo">
										{({ push, remove }) => (
											<>
												{values.extraInfo &&
													values.extraInfo.length > 0 &&
													values.extraInfo.map((info, index) => (
														<div
															className={classes.extraAttr}
															key={`${index}-info`}
														>
															<Field
																as={TextField}
																label={i18n.t("contactModal.form.extraName")}
																name={`extraInfo[${index}].name`}
																variant="outlined"
																margin="dense"
																fullWidth
																className={classes.textField}
															/>
															<Field
																as={TextField}
																label={i18n.t("contactModal.form.extraValue")}
																name={`extraInfo[${index}].value`}
																variant="outlined"
																margin="dense"
																fullWidth
																className={classes.textField}
															/>
															<IconButton
																size="small"
																onClick={() => remove(index)}
															>
																<TrashIcon />
															</IconButton>
														</div>
													))}
												<div className={classes.extraAction}>
													<Button
														className={classes.addExtraButton}
														variant="outlined"
														color="primary"
														onClick={() => push({ name: "", value: "" })}
													>
														{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
													</Button>
												</div>
											</>
										)}
									</FieldArray>
								</div>
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={onClose}
									color="secondary"
									disabled={isSubmitting || isLoading}
									variant="outlined"
									className={classes.btnWrapper}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting || isLoading}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{(isSubmitting || isLoading) && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ContactModal;
